// Shared "which date matters, and how late is it" rules for clinical records.
//
// Lifted out of `src/components/admin/home/NotificationsPanel.jsx` (TGCLOUD-1670)
// so the home Overdue panel and the pedigree data overlays cannot drift apart on
// what "overdue" means. The panel imports these back; its behaviour is unchanged
// except for RESOLVED_APPOINTMENT_STATUSES below.
//
// One deliberate change came with the lift. `isResolvedAppointment` used to test
// `appointmentStatus === 'Closed'` alone. The forms only ever wrote Open or
// Closed, and the Calendly sync wrote Open (it pulled bookings when run from
// Settings; no webhook ever reached it). Imports and the demo seeds, though,
// also wrote 'Attended' and 'Scheduled', and a status was missing altogether on
// pathway-created appointments. Treating 'Attended' and 'Cancelled' as
// unresolved made every attended appointment render as a red "Past due" row.
// They are resolved.
//
// A MISSING status is deliberately NOT resolved: an appointment nobody has closed
// out is exactly what an outstanding-appointment overlay is looking for.
//
// TGCLOUD-1751 replaced Open / Closed with Booked, Attended, Did Not Attend,
// Cancelled and Moved. Booked is the new Open (not resolved); the other four are
// resolved; Closed is kept, switched off, for the records that still hold it.
//
// TGCLOUD-1896: the home notification feed leaves resolved appointments out on
// the server (backend/services/notificationFeed/feed.js), with whatever else is
// done, so getBucket no longer sees one. The overlays still read
// isResolvedAppointmentStatus.

import { differenceInCalendarDays } from 'date-fns';
import { CLINICAL_COLOURS } from '../theme/palette';
import { isResolved } from './statusSemantics';

/**
 * The appointment statuses THIS PRODUCT SHIPS that mean "dealt with", in the
 * order the definitions list them, legacy Closed last.
 *
 * Kept exported because several places read it as prose, but it is no longer
 * the test: a site can configure its own wording, and `isResolvedAppointmentStatus`
 * below reads what a value MEANS instead. Use that, not this.
 */
export const RESOLVED_APPOINTMENT_STATUSES = Object.freeze([
  'Attended',
  'Did Not Attend',
  'Cancelled',
  'Moved',
  'Closed'
]);

/**
 * Case-insensitive membership test tolerant of a missing or padded value.
 *
 * Reads the value's MEANING rather than its wording (TGCLOUD-1784), so a site
 * that renames its resolved status to "Seen in clinic", or a Desktop customer
 * whose records say "Moved", is understood. The wording above is what this
 * product ships; anything else is learned from the site's own configured rows.
 *
 * A value nothing recognises is still NOT resolved, which is the behaviour this
 * function has always had and the safe direction: an appointment nobody has
 * closed out is exactly what an outstanding-appointment overlay looks for.
 */
export const isResolvedAppointmentStatus = (value) =>
  isResolved('appointmentStatuses', value);

/**
 * The date that decides whether a record is due, per record type.
 *
 * `n.type` is the notification-panel shape; `n.noteType` is the stored
 * discriminator. Both are accepted so the overlay evaluator can pass a raw note.
 */
export function getRelevantDate(n) {
  if (!n) return null;
  switch (n.type || n.noteType) {
    case 'Appointment':
      return n.appointmentDate;
    case 'Test':
      return n.reminderDate || n.dateResultDue;
    case 'Referral':
      return n.reminderDate;
    case 'Contact':
      return n.reminderDate || n.contactDate;
    case 'Clinical Note':
      return n.reminderDate || n.clinicalNoteDate;
    case 'Sample':
      return n.reminderDate || n.dateTaken;
    case 'Consent':
      return n.reminderDate || n.consentDate;
    default:
      return null;
  }
}

/**
 * How far ahead the notification feed looks, in the user's calendar days: the
 * panel's "This week" section and the bell's list alike (TGCLOUD-1896). The
 * server reads one day further (backend/services/notificationFeed/feed.js,
 * LOOK_AHEAD_DAYS, held to this by its test), so this is the cut that counts.
 *
 * There is no look-back limit. An item 90 days overdue used to drop out of the
 * panel as if dealt with; one nobody has acted on stays until somebody does.
 */
export const FUTURE_WINDOW_DAYS = 7;

export function isResolvedAppointment(n) {
  return (
    (n?.type || n?.noteType) === 'Appointment' &&
    isResolvedAppointmentStatus(n?.appointmentStatus)
  );
}

/**
 * Severity bands, shared by the panel's badges and the overlay's ring weight.
 * `days` is days PAST due (positive = late). Colours from the clinical register
 * (src/theme/palette.js).
 */
export const DUE_BANDS = Object.freeze([
  {
    minDays: 21,
    key: 'over2w',
    label: 'Past due > 2 weeks',
    bg: CLINICAL_COLOURS.alert
  },
  {
    minDays: 14,
    key: 'twoWeeks',
    label: 'Past due 2 weeks',
    bg: CLINICAL_COLOURS.pastDueTwoWeeks
  },
  {
    minDays: 7,
    key: 'oneWeek',
    label: 'Past due 1 week',
    bg: CLINICAL_COLOURS.pastDueOneWeek
  },
  { minDays: 1, key: 'due', label: 'Past due', bg: CLINICAL_COLOURS.pastDue }
]);

/**
 * Band for a number of days past due. Returns null when not yet late.
 * Used by the overlay to pick a ring stroke weight, and by the panel for badges.
 */
export function dueBand(days) {
  if (!Number.isFinite(days) || days < 1) return null;
  return DUE_BANDS.find((b) => days >= b.minDays) || null;
}

/**
 * Bucket a notification into overdue / today / coming, or null when it is due
 * further ahead than the feed looks. Overdue has no lower limit (TGCLOUD-1896).
 *
 * Everything that reaches it still needs acting on: the feed leaves out what is
 * done (TGCLOUD-1896). That ended the TGCLOUD-1751 grey badge that kept a
 * resolved past appointment on the panel with its own status, so every past
 * item is graded by how late it is.
 */
export function getBucket(n, now = new Date()) {
  const date = getRelevantDate(n);
  if (!date) return { section: 'today', badge: null, days: null };

  const days = differenceInCalendarDays(now, new Date(date));

  if (days > 0) {
    const band = dueBand(days);
    return {
      section: 'overdue',
      badge: { label: band.label, bg: band.bg },
      days
    };
  }
  if (days === 0) {
    return { section: 'today', badge: { label: 'Due', bg: '#3b9ae0' }, days };
  }
  if (days >= -FUTURE_WINDOW_DAYS) {
    return { section: 'coming', badge: null, days };
  }
  return null;
}
